/*
 * GardenPack addition to the Sternify cart drawer (aside.sd-drawer).
 *
 * Sternify replaces the theme's cart drawer and renders its lines client side,
 * so this reads the real cart (/cart.js) after each drawer render and shows the
 * bundle's included non-physical benefit (theme settings "GardenPack bundle
 * benefit") on a qualifying line: the configured product, added through the
 * bundle app (its line property is present), with at least the configured
 * quantity. Eligibility follows the cart, not the product-page picker, so it
 * stays right after refreshes and drawer quantity changes.
 * The benefit row is display only and can be switched off (config.showBenefit),
 * e.g. once the benefit is added to the cart as a real free gift product.
 *
 * It also keeps the bundle app's free gift honest: Kaching adds the gift line
 * but does not remove it when the bundle drops below the qualifying quantity in
 * Sternify's drawer, which would leave the gift charged at full price. A gift
 * line (its bundle property has a "gift" key) is removed when the bundle line
 * it belongs to (same "id", "main": true) is gone or below config.giftMinQty
 * (per-product overrides: config.gift2ProductId/gift2MinQty, e.g. the app gift
 * with 3 kits, and config.gift3ProductId/gift3MinQty, e.g. the guide with 1 kit).
 * Lines without the bundle app's gift marker (e.g. a separately bought Mini
 * Veg) are never touched. The gift is not re-added when the quantity goes back
 * up in the drawer: the bundle app intercepts cart/add requests and would turn a
 * re-add into a new bundle, so shoppers re-add the bundle from the product page.
 */
(() => {
  const configEl = document.getElementById('gp-cart-benefit-config');
  if (!configEl) return;
  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }
  if (!config.productId) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let timer = null;
  let request = null;
  let applying = false;

  const benefitMarkup = () => {
    const box = document.createElement('div');
    box.className = 'gp-cart-benefit';
    box.dataset.gpBenefit = '';
    box.innerHTML = config.iconHtml || '';
    const text = document.createElement('span');
    text.className = 'gp-cart-benefit__text';
    for (const [cls, value] of [['kind', config.kind], ['name', config.label], ['note', config.note]]) {
      if (!value) continue;
      const span = document.createElement('span');
      span.className = `gp-cart-benefit__${cls}`;
      span.textContent = value;
      text.append(span);
    }
    box.append(text);
    return box;
  };

  const lineFor = (lines, item, index) => {
    // Sternify lists lines in cart order; confirm with the title before trusting the index.
    const byIndex = lines[index];
    const title = (el) => (el && el.querySelector('.sd-line__title')?.textContent.trim()) || '';
    if (byIndex && title(byIndex).startsWith(item.product_title)) return byIndex;
    return lines.find((el) => title(el).startsWith(item.product_title)) || null;
  };

  const bundleData = (item) => {
    const raw = item.properties && item.properties[config.property];
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (error) { return null; }
  };

  // Units of the bundle needed to keep a given gift: per-product overrides
  // (gift2: e.g. the app with 3 kits; gift3: e.g. the guide with 1 kit), else giftMinQty.
  const minFor = (productId) => {
    if (config.gift2ProductId && productId === config.gift2ProductId) return config.gift2MinQty || config.giftMinQty;
    if (config.gift3ProductId && productId === config.gift3ProductId) return config.gift3MinQty || 1;
    return config.giftMinQty;
  };

  // Returns true when it removed a gift line (the cart then needs re-reading).
  let syncing = false;
  async function syncGifts(cart) {
    if (syncing || !config.giftMinQty) return false;
    const stale = cart.items.filter((item) => {
      const data = bundleData(item);
      if (!data || !data.gift) return false;
      const main = cart.items.find((other) => {
        const d = bundleData(other);
        return d && d.main && d.id === data.id;
      });
      const min = minFor(item.product_id);
      return !main || main.quantity < min;
    });
    if (!stale.length) return false;
    syncing = true;
    try {
      for (const item of stale) {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
      window.SternifyCart?.loadCart?.();
      document.dispatchEvent(new CustomEvent('gp:cart-gift-removed'));
    } catch (error) {
      // Leave the cart as it is; the next drawer render retries.
    } finally {
      syncing = false;
    }
    return true;
  }

  async function apply() {
    request?.abort();
    request = new AbortController();
    let cart;
    try {
      cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal: request.signal })).json();
    } catch (error) {
      return;
    }
    if (await syncGifts(cart)) return; // the drawer re-renders and calls apply again
    const list = document.querySelector('.sd-drawer .sd-cart__list');
    if (!list) return;
    const lines = [...list.querySelectorAll('.sd-line')];
    applying = true;
    cart.items.forEach((item, index) => {
      const line = lineFor(lines, item, index);
      if (!line) return;
      const isProduct = item.product_id === config.productId;
      const fromBundle = Boolean(item.properties && item.properties[config.property]);
      const eligible = config.showBenefit && Boolean(config.label) && isProduct && fromBundle && item.quantity >= config.minQty;
      const existing = line.querySelector('[data-gp-benefit]');
      if (eligible && !existing) line.querySelector('.sd-line__body')?.append(benefitMarkup());
      if (!eligible && existing) existing.remove();
    });
    applying = false;
  }

  const schedule = () => {
    if (applying) return;
    clearTimeout(timer);
    timer = setTimeout(apply, 150);
  };

  const isOurs = (node) => node.nodeType === 1 && node.matches('[data-gp-benefit]');
  const inDrawer = (record) => {
    const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
    if (target && target.closest('.sd-drawer')) return true;
    return [...record.addedNodes].some((n) => n.nodeType === 1 && (n.matches('.sd-drawer') || n.querySelector('.sd-drawer')));
  };

  const observer = new MutationObserver((records) => {
    // React only to Sternify's drawer renders, never to the rows this script inserts
    // (or to unrelated page updates such as the delivery countdown).
    const relevant = records.some((r) => inDrawer(r) && ![...r.addedNodes, ...r.removedNodes].every(isOurs));
    if (relevant) schedule();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  schedule();
})();
